/**
 * Pure presentation/model helpers for the Sourcing + Recruiter Teams tabs
 * (E05-F05-T04).
 *
 * Kept DB-free and DOM-free so the dialog/card rules (which the e2e relies on)
 * can be unit-tested in isolation. 1:1 port of the legacy `dashboard.js`
 * saveSrcTeam member-collection, renderSourcingTeams chip text, renderTeams
 * teamlead/members rendering and openTeamDialog teamlead option filtering.
 */
import type { SourcingTeamMember } from '@eer/shared';

/** A row in the sourcing-team members editor (may be partially filled). */
export interface MemberDraft {
  readonly name: string;
  readonly email: string;
}

/**
 * Collect only the member rows where BOTH name and email are non-empty, trimming
 * each (1:1 with legacy `saveSrcTeam`: `if (n && e) members.push({name:n,email:e})`).
 * Empty/partial rows in the dialog are dropped before save (T04 acceptance #2).
 */
export function collectMembers(rows: readonly MemberDraft[]): SourcingTeamMember[] {
  const out: SourcingTeamMember[] = [];
  for (const row of rows) {
    const name = row.name.trim();
    const email = row.email.trim();
    if (name && email) out.push({ name, email });
  }
  return out;
}

/**
 * The chip label for one sourcing-team member — legacy `${m.name} <${m.email}>`
 * (the legacy HTML escaped the angle brackets; React escapes for us). T04 #1.
 */
export function memberChipLabel(member: SourcingTeamMember): string {
  return `${member.name} <${member.email}>`;
}

/** The "(N members)" count suffix on a sourcing-team card heading. */
export function memberCountLabel(count: number): string {
  return `(${count} members)`;
}

/**
 * The Team Lead display name for a recruiter-team card — the joined
 * `teamlead_name`, or the legacy "—" placeholder when no lead is assigned
 * (renderTeams: `const tlName = t.teamlead_name || '—'`). T04 #3.
 */
export function teamLeadLabel(teamleadName: string | null): string {
  return teamleadName && teamleadName.trim() ? teamleadName : '—';
}

/** One option in the recruiter-team teamlead `<select>` (`Name (role)`). */
export interface TeamLeadOption {
  readonly id: string;
  readonly label: string;
}

/** The minimal user shape the teamlead picker needs. */
export interface TeamLeadCandidate {
  readonly id: string;
  readonly name: string;
  readonly role: string;
}

/**
 * Build the teamlead `<select>` options from the users list, EXCLUDING the
 * service user (role==='system') — 1:1 with legacy openTeamDialog
 * (`users.filter(u => u.role !== 'system').map(u => `<option …>${u.name} (${u.role})`)`).
 * T04 #3 ("select тимлида исключая role=system").
 */
export function teamLeadOptions(users: readonly TeamLeadCandidate[]): TeamLeadOption[] {
  return users
    .filter((u) => u.role !== 'system')
    .map((u) => ({ id: u.id, label: `${u.name} (${u.role})` }));
}
